/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext, useState, useCallback } from 'react';
import { AuthContext, TeamContext } from 'contexts';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import Swal from 'sweetalert2';
import {
  Title,
  Text,
  Form,
  Switch,
  List,
  ListItem,
  ListItemMeta,
  Avatar,
  Button,
  Icon,
  Row,
  Col,
  Spin,
  message,
  Divider,
} from 'components';
import errorReporter from '@app/errorReporter';
import {
  update as updateTeam,
  deleteTeamDefaultContact,
  applyTeamDefaultContact,
} from 'api/TeamController';
import {
  ContactModalContextProvider,
  ContactModalContextProviderProps,
} from '../ContactModal/ContactModalContext';
import ContactModal from '../ContactModal';
import { TeamDefaultContactType } from '../types';

const ListingSettingsTab = () => {
  const history = useHistory();
  const { auth } = useContext(AuthContext);
  const {
    targetTeamState,
    setActiveTab,
    setTargetTeamState,
    contacts,
    setContacts,
    targetTeamId,
  } = useContext(TeamContext);
  const { users } = targetTeamState;
  const [saving, setSaving] = useState<boolean>(false);

  const handleSave = useCallback(async () => {
    setSaving(true);
    const updates = { ...targetTeamState };
    delete updates.users;
    try {
      const result = await updateTeam(updates.id, updates);
      if (result.status === 200) {
        message.success('Team Details updated successfully.');
      }
    } catch (err) {
      const msg = `Could not update Team Details`;
      errorReporter.error(msg);
      message.error(msg);
    }
    setSaving(false);
  }, [targetTeamState]);

  const navigateBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const defaultContactModalProps: ContactModalContextProviderProps = {
    visible: false,
    modeType: 'new',
    contact: null,
  };
  const [contactModalProps, setContactModalProps] =
    useState<ContactModalContextProviderProps>(defaultContactModalProps);

  const deleteContactAlert = async (contact: TeamDefaultContactType) => {
    const isDeleteConfirm = await Swal.fire({
      title: 'Delete Contact',
      text: `Are you sure you want to delete ${contact.firstName} ${contact.lastName}?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Delete',
    });
    if (isDeleteConfirm.value) {
      try {
        await deleteTeamDefaultContact(targetTeamId, contact.contactId);
        setContacts(
          contacts.filter(({ contactId }) => contactId !== contact.contactId),
        );
        message.success(`Contact deleted successfully`);
        await applyDeleteContactAlert(contact.contactId);
      } catch (err) {
        message.error('Error deleting user');
        errorReporter.error(err);
      }
    }
  };

  const applyDeleteContactAlert = async (contactId: string) => {
    const isApplyConfirm = await Swal.fire({
      title: 'Delete Contact',
      text: `Apply this change to all existing listings?`,
      type: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3ea800',
      confirmButtonText: 'Apply',
    });
    if (isApplyConfirm.value) {
      try {
        const applySettings = {
          team: auth.team,
          isDelete: true,
          contactId,
        };
        const { agentsWithCommonDefaultContacts } =
          await applyTeamDefaultContact(
            auth.team!.toString(),
            applySettings,
            auth.org.id,
          );
        if (agentsWithCommonDefaultContacts.length) {
          const agentsNameStr = agentsWithCommonDefaultContacts.join(', ');
          showWarningModal(agentsNameStr);
        }
        message.success(`Listings updated successfully`);
      } catch (err) {
        errorReporter.error(`Failed to apply changes: ${err}`);
      }
    }
  };

  const showWarningModal = async (agentsNameStr: string) => {
    await Swal.fire({
      title: "Exists on Agent's Settings",
      html: `<p>Please note this contact is still listed under the following agent's Account Settings: <strong>${agentsNameStr}</strong></p>
        <p>This contact will be applied as a default listing contact for the agent's future listings.</p>
        `,
      type: 'warning',
      showCancelButton: false,
      confirmButtonColor: '#C4C4C4',
      confirmButtonText: 'OK',
    });
  };

  const handleAddContact = () =>
    setContactModalProps({ visible: true, modeType: 'new', contact: null });
  const handleEditContact = (contact: TeamDefaultContactType) =>
    setContactModalProps({ visible: true, modeType: 'edit', contact });

  const handelCancel = () => navigateBack();
  return (
    <>
      <Form layout="vertical">
        <Title level={3} hideMargin>
          Listing Notifications
        </Title>

        <StyledSwitchGrid>
          <Switch
            checked={
              targetTeamState?.email &&
              targetTeamState?.sendNotificationToTeamEmail
            }
            onChange={checked => {
              if (checked && !targetTeamState?.email)
                message.error(
                  'Please add a team email to turn on the notifications.',
                );
              setTargetTeamState((curr: any) => ({
                ...curr,
                sendNotificationToTeamEmail: targetTeamState.email && checked,
              }));
            }}
          />
          <div>
            Send all listing notifications to the main team email address{' '}
            {targetTeamState.email || (
              <Button type="link" onClick={() => setActiveTab('DETAILS')}>
                + Add Team Email
              </Button>
            )}
          </div>
        </StyledSwitchGrid>
      </Form>
      <Divider />

      <Title level={3}>Default Showing Configuration Contacts</Title>

      <Text type="secondary">
        Add the following users to all of my team’s listing showing
        configurations by default:
      </Text>

      <List
        footer={
          <Button
            type="dashed"
            block
            icon={<StyledIcon type="plus" offset={[0, 1]} />}
            onClick={handleAddContact}
          >
            Add Contact
          </Button>
        }
      >
        {contacts.map((user, index) => (
          <ListItem
            key={user.contactId}
            actions={[
              <Row key="actions" align="middle" gutter={16}>
                <Col>
                  <Button type="link" onClick={() => handleEditContact(user)}>
                    Edit
                  </Button>
                </Col>
                <Col>
                  <StyledXButton
                    type="primary"
                    shape="circle"
                    icon={<StyledIcon type="x" width="12px" height="12px" />}
                    onClick={() => deleteContactAlert(user)}
                  />
                </Col>
              </Row>,
            ]}
          >
            <ListItemMeta
              avatar={<Avatar user={user} />}
              title={
                <Text block>
                  {user.firstName} {user.lastName}
                </Text>
              }
              description={user.addressName}
            />
          </ListItem>
        ))}

        <ContactModalContextProvider {...contactModalProps}>
          <ContactModal
            onClose={() => setContactModalProps(defaultContactModalProps)}
          />
        </ContactModalContextProvider>
      </List>
      <Divider />
      <BottomButton
        data-cy="team-button-save"
        disabled={saving}
        onClick={handleSave}
        type="primary"
      >
        Save Changes
      </BottomButton>
      <BottomButton data-cy="team-button-cancel" onClick={handelCancel}>
        Cancel
      </BottomButton>
    </>
  );
};

export default ListingSettingsTab;

const StyledSwitchGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 20px 10px;
  margin: 20px 0px;
`;

const StyledIcon = styled(Icon)`
  && {
    color: inherit;
    transition: none;
  }
`;

const StyledXButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
`;
const BottomButton = styled(Button)`
  &:not(:last-child) {
    margin-right: 16px;
  }
`;
